/**
 * Profile backend capabilities the redesign needs but the API lacks (BE-147).
 * Off → the UI runs its mock in dev and hides the action in a production build,
 * so a deployed stand never claims "link sent" when nothing was sent.
 * On → the real call. Enable per capability as the backend ships:
 *   VITE_PROFILE_API="change_email,change_password"   (or "all")
 */
export const PROFILE_API_CAPABILITIES = [
  /** `POST /auth/change-email` — a confirmation link to the new address */
  'change_email',
  /** `POST /auth/change-password` — current + new password */
  'change_password',
  /** `email_verified` in profile-data + `POST /auth/resend-verification` */
  'email_verification',
  /** `auth_provider` in profile-data — password vs Google */
  'auth_provider',
] as const

export type ProfileApiCapability = (typeof PROFILE_API_CAPABILITIES)[number]

function parseProfileApiEnv(raw: string | undefined): ReadonlySet<ProfileApiCapability> {
  const tokens = (raw ?? '')
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean)
  if (tokens.includes('all')) {
    return new Set(PROFILE_API_CAPABILITIES)
  }
  return new Set(
    tokens.filter((token): token is ProfileApiCapability =>
      (PROFILE_API_CAPABILITIES as readonly string[]).includes(token),
    ),
  )
}

const enabled = parseProfileApiEnv(import.meta.env.VITE_PROFILE_API)

export function hasProfileApi(capability: ProfileApiCapability): boolean {
  return enabled.has(capability)
}

/** The action is offered: real API, or its mock on a dev server. */
export function isProfileActionAvailable(capability: ProfileApiCapability): boolean {
  return hasProfileApi(capability) || import.meta.env.DEV
}
